import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSupabaseToken } from '../../hooks/useSupabaseToken'
import i18n, { joinSentences, useLanguageTick } from '../../i18n'
import { trackEvent } from '../../lib/analytics'
import { currentCampaign, fetchReport, parseReport, readFamilyLabel, type WatchtowerPhase } from '../../lib/watchtower'
import { claimRootPrompt, releaseRootPrompt, type RootPromptId } from '../../lib/rootPrompt'
import { popupPickSentence } from '../../lib/watchtowerNudges'
import {
  campaignLinkOwnsClaim,
  isWatchtowerRoute,
  markPopupShown,
  popupDecisionOnce,
  popupWasShown,
  resolveLaunchPopup,
} from '../../lib/watchtowerPopup'
import { OVERLAY_ABOVE_AD, PANEL_FITS_OVERLAY } from '../../lib/adInset'

/**
 * The one-time launch popup.
 *
 * Mounted at the app root because it is not about any page: a signed-in person
 * gets told once what Watchtower is and that it has looked at their decks,
 * wherever they happen to be. The decision itself lives in
 * `watchtowerPopup.ts`; this file asks it once per app load and draws the
 * answer. The button opens the ARCHIVE (`/watchtower`), not the one report the
 * claim resolved: the popup explains the feature, and the archive is where all
 * of that person's reports live.
 *
 * EXCEPT when it names a pick (2026-09-24, Watchtower nudges part A). A
 * decoded report with at least one pick and a nameable deck turns the body
 * into "<pick> fits your <deck> deck." and the button into "See the report",
 * which opens THAT report. Anything short of that (zero picks, an unreadable
 * body, a failed read, or the moment before the read lands) keeps the generic
 * copy and the archive button exactly as before. Both events carry
 * `named_pick` so the two open rates can be compared, which means the SHOWN
 * event now fires once the read has settled (the copy is final), not on first
 * paint; a popup closed before that still fires it, with `named_pick: false`,
 * because that is what the person saw. The shown MARK is unchanged: written on
 * render.
 *
 * "Once per app load" rather than once per stored session on purpose. A
 * `try_again` (a publish was holding the generation row) has to be re-asked at
 * some point, and the next open is exactly when. The permanent half of the
 * guard is the localStorage mark, which the decision owns.
 *
 * Watchtower's own routes outrank the popup (2026-09-11). A person who lands
 * on `/watchtower/<report>` from the launch email, or on `/watchtower?campaign=`
 * from a campaign link, is already looking at what the popup exists to
 * announce, and the modal was drawing itself on top of the report the link
 * had just opened. Three rules, one per way that can happen:
 *
 * - A campaign link's PAGE owns the claim. This component makes none on
 *   that URL, so the load is one claim, and the page writes the shown mark
 *   when its claim opens a report.
 * - On any other Watchtower route - a report, or the bare archive, which is
 *   where this popup's own button goes - the decision resolves as usual (the
 *   claim is recorded) and a `show` is marked shown when the page is the
 *   archive or that claim's own report, because reaching it on your own IS
 *   the telling; on any other report nothing is marked. Either way nothing
 *   is rendered and no `watchtower_popup_shown` fires. A campaign link
 *   reached while the campaign or the decision is still resolving takes the
 *   claim over: the root flow stops there, unclaimed and unmarked.
 * - A popup already up when the route becomes a Watchtower route is taken
 *   down. The mark was written when it rendered, so nothing re-asks.
 */

// This popup's slot in the one-root-prompt-per-load arbitration (a code).
const WATCHTOWER_PROMPT_ID: RootPromptId = 'watchtower'

// The phases this build knows how to word. A decoded body is typed, but the
// type is a promise about the server, not a check: a missing or new value
// reads as unknown, and unknown renders the neutral copy.
const KNOWN_PHASES: readonly WatchtowerPhase[] = ['prerelease', 'release_day', 'set_check']
function knownPhase(value: unknown): WatchtowerPhase | null {
  return KNOWN_PHASES.includes(value as WatchtowerPhase) ? (value as WatchtowerPhase) : null
}

interface PopupState {
  /**
   * The signed-in user whose claim produced this popup. Rendered, and its
   * buttons handled, only while that is still the session: an account switch
   * under a live popup closes it instead of showing A's pick to B.
   */
  ownerId: string
  /**
   * The campaign this popup's claim was made under, captured when the
   * campaign resolved. The shown mark and every action use THIS value, never a
   * fresh read: if the server's current campaign moves on while the popup is
   * up, the mark still has to land on the campaign the person was told about.
   */
  campaign: string
  reportId: string
  runId: string | null
  /**
   * `header.family_label` off the resolved report, exactly the eyebrow the
   * report page prints. Null until the read lands, null when the label is
   * missing or blank, and null forever for a version this build cannot
   * decode. Without it the "<label> is coming." (or, on a release-day run,
   * "<label> is out.") sentence is dropped, never
   * replaced by the family id.
   */
  familyLabel: string | null
  /**
   * `header.phase` off the same decoded report. It picks the tense: a
   * release-day run says "<label> is out." and "the new set", a prerelease
   * run keeps "<label> is coming." and "the upcoming set". Null until the read
   * lands, and null for a failed read, a body this build cannot decode, or a
   * phase value this build does not know. Unknown NEVER asserts a tense: it
   * renders the neutral "Watchtower checked your decks." and no label
   * sentence, so a release-day popup closed before its read lands (it is
   * marked shown on render, so that is the only copy that person ever sees)
   * cannot have said "upcoming".
   */
  phase: WatchtowerPhase | null
  /**
   * The report's `cta` flag, the same boolean the report page keys its Pro
   * band on. True means this set is free and the popup says so. Null
   * until the read lands and null for a body we could not decode: unknown is
   * not false, but it is not "free" either, so the line stays off.
   */
  cta: boolean | null
  /**
   * The "<pick> fits your <deck> deck." sentence off a DECODED report, or null
   * for the generic body and the archive button. Never set from anything but
   * `popupPickSentence` over a `parseReport` ok body.
   */
  pickSentence: string | null
  /** The enrichment read has come back (either way). Until then the copy is
   *  provisional and the shown event waits. */
  settled: boolean
}

// The popup's copy is in `common:watchtower_popup`, not the `watchtower`
// namespace: this component is mounted eagerly at the app root, so a
// `watchtower` lookup here would pull that whole catalog into the chunk every
// page loads.
export default function WatchtowerLaunchPopup() {
  useLanguageTick()
  const { userId } = useSupabaseToken()
  const navigate = useNavigate()
  const location = useLocation()
  const [popup, setPopup] = useState<PopupState | null>(null)
  const trackedRef = useRef<string | null>(null)
  // The popup as last committed, for the close path, which also runs from
  // callbacks (root-prompt eviction) that cannot read render state.
  const popupRef = useRef<PopupState | null>(null)
  // The report id whose modal has actually rendered (and been marked shown).
  const markedRef = useRef<string | null>(null)
  // Enrichment generation. Every close bumps it, and a report read that comes
  // back under an older generation is dropped, so a late read can never
  // resurrect a popup that was answered, evicted or taken down.
  const genRef = useRef(0)

  const commit = useCallback((next: PopupState | null) => {
    popupRef.current = next
    setPopup(next)
  }, [])

  // One shown event per report, carrying what the person actually read.
  const trackShown = useCallback((p: PopupState, namedPick: boolean) => {
    if (trackedRef.current === p.reportId) return
    trackedRef.current = p.reportId
    trackEvent('watchtower_popup_shown', {
      report_id: p.reportId,
      run_id: p.runId,
      surface: 'web',
      named_pick: namedPick,
    })
  }, [])

  // THE close. Every way the popup goes away comes through here: Later, the
  // backdrop, Open, a root-prompt eviction, a Watchtower route taking over,
  // and an account switch. It counts the impression if the modal was ever on
  // screen (as whatever it showed at that moment), invalidates any pending
  // enrichment, frees the slot (a no-op when evicted, since the evictor holds
  // it now) and clears the state.
  const closePopup = useCallback(() => {
    const p = popupRef.current
    genRef.current += 1
    if (p && markedRef.current === p.reportId) trackShown(p, p.settled && p.pickSentence !== null)
    releaseRootPrompt(WATCHTOWER_PROMPT_ID)
    commit(null)
  }, [commit, trackShown])

  // Where the person is when the decision LANDS, not when it was asked. The
  // effect below keys on the user only, so a route change never re-asks; the
  // ref lets the one resolution read the current route. A LAYOUT effect,
  // because a passive one runs after paint and a resolved promise's
  // continuation can run before it: the ref would still say the old route
  // and the modal would draw over Watchtower for exactly the navigation this
  // exists to catch.
  const routeRef = useRef({ pathname: location.pathname, search: location.search })
  useLayoutEffect(() => {
    routeRef.current = { pathname: location.pathname, search: location.search }
  }, [location.pathname, location.search])

  // The signed-in user as last rendered, for the async resolution below to
  // re-check against after each await (belt and braces with `cancelled`, which
  // the effect cleanup sets on a user change).
  const userRef = useRef(userId)
  useLayoutEffect(() => {
    userRef.current = userId
  }, [userId])

  useEffect(() => {
    if (!userId) return
    // A campaign link's page makes the claim. Nothing is asked, memoised or
    // marked here; see the header comment.
    if (campaignLinkOwnsClaim(routeRef.current.pathname, routeRef.current.search)) return

    let cancelled = false
    // Re-asked after every await, against the route as it is NOW. A campaign
    // link reached while discovery or the decision was pending owns the claim
    // from then on (Codex r1, 2026-10-31): the page opens ITS campaign, and
    // the root flow must neither claim nor mark anything, or the current
    // campaign's popup is spent on a page that showed a different one.
    const linkOwnsClaim = () => campaignLinkOwnsClaim(routeRef.current.pathname, routeRef.current.search)
    void (async () => {
      // The campaign comes from the server (one RPC per user per page
      // session). It is resolved FIRST and captured here for the rest of this
      // run: the decision, the shown mark and the popup state all use this
      // value. Null - no live campaign, the RPC missing or failing, a
      // malformed id - is no popup and no mark.
      const campaign = await currentCampaign(userId)
      if (cancelled || userRef.current !== userId || !campaign) return
      if (linkOwnsClaim()) return
      // One resolution per app load per (campaign, user), however many times
      // this mounts: a StrictMode replay awaits the same promise the cancelled
      // first run started, instead of exiting at a guard and never drawing.
      const decision = await popupDecisionOnce(
        campaign, userId, () => resolveLaunchPopup(campaign, userId),
      )
      if (cancelled || userRef.current !== userId) return
      if (linkOwnsClaim()) return
      // A decision that will NOT show anything (token_mismatch) is marked
      // here. A decision that WILL show is marked only when the modal actually
      // renders (the effect below), never at resolve time: the iOS twin marked
      // at resolve and lost the one-time offer whenever a sheet was already up
      // or the view went away first. Same rule here, so an unmount between
      // resolve and render (cancelled) leaves the next load free to retry.
      if (decision.markShown && !decision.show) markPopupShown(campaign, userId)
      if (!decision.show || !decision.reportId) return
      // The memoised decision outlives a render. A remount after the modal has
      // been drawn once this load reads the persisted mark and stops here.
      if (popupWasShown(campaign, userId)) return
      // Deep-link precedence. On a Watchtower route the page is the telling:
      // draw nothing (see the header comment). The mark is written only when
      // the page on screen is showing THIS campaign's report: the bare
      // archive (which lists it) or that report itself. Any other report is
      // not the telling for this campaign, so it is left unmarked and the
      // next load asks again.
      const { pathname } = routeRef.current
      if (isWatchtowerRoute(pathname)) {
        if (pathname === '/watchtower' || pathname === `/watchtower/${decision.reportId}`) {
          markPopupShown(campaign, userId)
        }
        return
      }
      // Shown immediately with what the claim resolved - report id AND run id
      // both come back on the claim, so both analytics events are complete
      // whatever happens next. The report read below adds the family label,
      // the free line and the named pick, and it decides the phase, which
      // picks the tense ("is coming" / "is out"). Until it lands, and forever
      // if it fails, the body is the phase-neutral line. A failed read costs
      // a shorter, tenseless paragraph, never the popup.
      // One root prompt per load. This outranks the email opt-in and will take
      // the slot from it; it yields only to password recovery, which is the
      // reason that person is on the page at all. Losing the slot costs
      // nothing permanent - the shown mark is written on render, below, so the
      // offer is re-asked on the next load.
      //
      // An eviction goes through the central close, so the impression is
      // counted and the pending read below is invalidated.
      if (!claimRootPrompt(WATCHTOWER_PROMPT_ID, () => closePopup())) return
      const base = { ownerId: userId, campaign, reportId: decision.reportId, runId: decision.runId }
      commit({ ...base, familyLabel: null, phase: null, cta: null, pickSentence: null, settled: false })
      const gen = genRef.current
      const row = await fetchReport(decision.reportId, userId)
      // Dropped when this run was superseded (user change) OR the popup was
      // closed while the read was in flight.
      if (cancelled || genRef.current !== gen) return
      if (!row) {
        commit({ ...base, familyLabel: null, phase: null, cta: null, pickSentence: null, settled: true })
        return
      }
      // The body goes through the version gate before anything is read off it.
      // Reaching into `header` directly is what would turn a report from a
      // later build into an unhandled rejection here, on a component mounted
      // at the app root.
      const parsed = parseReport(row.report_json)
      if (parsed.kind === 'ok') {
        commit({
          ...base,
          familyLabel: readFamilyLabel(parsed.report),
          phase: knownPhase(parsed.report.header?.phase),
          cta: parsed.report.cta === true,
          pickSentence: popupPickSentence(parsed.report),
          settled: true,
        })
      } else {
        // Nothing is quoted off a body we cannot decode: no label, and the
        // free line stays off because `cta` is unknown, not false. That is the
        // whole unsupported-version story now; nothing else keys on it.
        commit({ ...base, familyLabel: null, phase: null, cta: null, pickSentence: null, settled: true })
      }
    })()
    return () => { cancelled = true }
  }, [userId, closePopup, commit])

  // Account switch (or sign-out) under a live popup: it belongs to the user
  // who produced it. Close it through the one close path, which counts what
  // was shown, invalidates enrichment and frees the slot. Same lint exemption
  // as the route close below: the auth session is the external system this
  // synchronises with, and the render guard already hides the stale popup
  // for the one frame before this runs.
  useEffect(() => {
    const p = popupRef.current
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (p && p.ownerId !== userId) closePopup()
  }, [userId, closePopup])

  // The persisted "shown" mark is written on the first render of a report's
  // modal: it is on screen now. Unchanged by the named-pick copy.
  useEffect(() => {
    if (!popup || popup.ownerId !== userId || markedRef.current === popup.reportId) return
    markedRef.current = popup.reportId
    markPopupShown(popup.campaign, popup.ownerId)
  }, [popup, userId])

  // The shown event fires once the copy is final (the read has settled) so
  // `named_pick` says what the person actually read, or earlier from the
  // close path, whichever comes first.
  useEffect(() => {
    if (!popup || !popup.settled || popup.ownerId !== userId) return
    trackShown(popup, popup.pickSentence !== null)
  }, [popup, userId, trackShown])

  // The route became a Watchtower route under a live popup (the person
  // clicked through to the archive some other way, or a deep link landed
  // after the decision had already drawn). The report is the telling now;
  // take the modal down and free the slot. The mark was written on render.
  // Same shape as NavMenu's close-on-route-change, and the same lint
  // exemption: the route is the external system this synchronises with.
  const onWatchtowerRoute = isWatchtowerRoute(location.pathname)
  useEffect(() => {
    if (!popup || !onWatchtowerRoute) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    closePopup()
  }, [popup, onWatchtowerRoute, closePopup])

  if (!popup || onWatchtowerRoute || popup.ownerId !== userId) return null

  // What is on screen right now. Only a settled, decoded read can name a pick.
  const namedPick = popup.settled && popup.pickSentence !== null

  // Every way this closes goes through here. It was three separate
  // setPopup(null) calls, and the review found the one that had been missed:
  // "Later", the button most people press, held the slot for the rest of the
  // load and silently suppressed the next prompt.
  // A close before the read settled still counts as shown, as the generic
  // copy the person saw (closePopup reads the committed state).
  const dismiss = closePopup

  const open = () => {
    // Shown before opened, always, so the funnel never has an open without a
    // show.
    trackShown(popup, namedPick)
    trackEvent('watchtower_popup_opened', {
      report_id: popup.reportId,
      run_id: popup.runId,
      surface: 'web',
      named_pick: namedPick,
    })
    dismiss()
    // A named pick opens the report that named it. Otherwise the archive,
    // not the resolved report; the event above still carries the report and
    // run ids the claim resolved, so the funnel is unchanged.
    navigate(namedPick ? `/watchtower/${popup.reportId}` : '/watchtower')
  }

  // The tense follows the run, and only a phase the read actually returned
  // picks one. A prerelease run keeps the original "is coming" copy byte for
  // byte. A release-day run (and a set check, which is never claimed through
  // this popup but would be about a set that is out) says the set is out.
  // Unknown - the read in flight, failed, undecodable, or a phase this build
  // does not know - asserts neither: no label sentence, and the neutral
  // "Watchtower checked your decks."
  const released = popup.phase === 'release_day' || popup.phase === 'set_check'
  const prerelease = popup.phase === 'prerelease'
  const labelSentence = !popup.familyLabel
    ? null
    : released
      ? i18n.t('common:watchtower_popup.out', { label: popup.familyLabel })
      : prerelease
        ? i18n.t('common:watchtower_popup.coming', { label: popup.familyLabel })
        : null
  const checkedSentence = released
    ? i18n.t('common:watchtower_popup.generic_checked_released')
    : prerelease
      ? i18n.t('common:watchtower_popup.generic_checked')
      : i18n.t('common:watchtower_popup.generic_checked_pending')
  const body = joinSentences(
    [
      labelSentence,
      ...(namedPick ? [popup.pickSentence] : [checkedSentence, i18n.t('common:watchtower_popup.generic_names')]),
    ].filter((s): s is string => s !== null),
  )

  return (
    <div
      data-testid="watchtower-popup"
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 ${OVERLAY_ABOVE_AD}`}
      onClick={dismiss}
    >
      <div className={`surface p-6 max-w-md w-full space-y-4 ${PANEL_FITS_OVERLAY}`} onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-white">{i18n.t('common:watchtower_popup.title')}</h3>
        {/*
          The generic body explains the feature and says nothing about what
          any one report found. The family label is quoted exactly as the
          report page prints it; a read that has not landed, a blank label, or
          a version this build cannot decode all drop that sentence and keep
          the rest. The one other thing ever quoted is the first pick and the
          deck, and only off a decoded report (see the header comment). The free line is keyed on the same
          `cta` boolean the report page keys its Pro band on, and stays off
          while that is unknown.
        */}
        <p data-testid="watchtower-popup-body" className="text-sm text-gray-400">
          {body}
        </p>
        {popup.cta === true && (
          <p data-testid="watchtower-popup-free" className="text-sm text-gray-400">
            {i18n.t('common:watchtower_popup.free')}
          </p>
        )}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={dismiss}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
          >
            {i18n.t('common:watchtower_popup.later')}
          </button>
          <button
            type="button"
            onClick={open}
            className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            {namedPick ? i18n.t('common:watchtower_popup.see_report') : i18n.t('common:watchtower_popup.open')}
          </button>
        </div>
      </div>
    </div>
  )
}
